import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import Button from "@/components/editorial/Button";

function Logo({ onDark }) {
  return (
    <Link to="/" data-testid="brand-logo" className="inline-flex items-baseline leading-none">
      <span
        className={`font-serif italic text-xl md:text-[22px] tracking-tight ${
          onDark ? "text-cream" : "text-charcoal"
        }`}
      >
        Mujeres
      </span>
      <span className="mx-1 text-terracotta font-serif text-2xl leading-none">·</span>
      <span
        className={`font-serif text-xl md:text-[22px] tracking-tight ${
          onDark ? "text-cream" : "text-charcoal"
        }`}
      >
        Motor
      </span>
    </Link>
  );
}

export default function Header({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const onDark = transparent && !scrolled;
  const shell = onDark
    ? "bg-transparent"
    : "bg-cream/85 backdrop-blur-[18px] border-b border-charcoal/10";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-40 transition-colors duration-[400ms] ${shell}`}
      >
        <div className="mx-auto max-w-[1440px] px-6 sm:px-8 md:px-12 lg:px-16">
          <div className="flex items-center justify-between py-5">
            <Logo onDark={onDark} />

            <nav className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map(({ label, href }) => (
                <Link
                  key={href}
                  to={href}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-sm tracking-wide transition-opacity duration-300 hover:opacity-60 ${
                    onDark ? "text-cream" : "text-charcoal"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <Button
                to="/nominate"
                variant={onDark ? "outlineCream" : "primary"}
                className="hidden md:inline-flex !py-2.5 !px-5 !text-xs"
                data-testid="header-nominate-cta"
              >
                Postula una historia
              </Button>
              <button
                type="button"
                data-testid="menu-toggle"
                onClick={() => setOpen((v) => !v)}
                aria-label="Abrir menú"
                aria-expanded={open}
                className={`lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border transition-colors ${
                  onDark
                    ? "border-cream/40 text-cream hover:bg-cream/10"
                    : "border-charcoal/20 text-charcoal hover:bg-charcoal/5"
                }`}
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <Link
                to="/admin/login"
                data-testid="header-admin-link"
                className={`hidden md:inline text-[10px] uppercase tracking-[0.25em] ${
                  onDark ? "text-cream/60 hover:text-cream" : "text-charcoal/50 hover:text-charcoal"
                }`}
              >
                Admin
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-30 bg-charcoal text-cream flex flex-col animate-fade-up"
        >
          <div className="flex-1 flex flex-col justify-center px-8 gap-5 pt-20">
            {NAV_LINKS.map(({ label, href }, i) => (
              <Link
                key={href}
                to={href}
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-serif text-4xl sm:text-5xl font-light hover:text-terracotta transition-colors"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                {label}
              </Link>
            ))}
          </div>
          <div className="px-8 pb-12 border-t border-cream/10 pt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <Button to="/nominate" variant="primary" data-testid="mobile-nominate-cta">
              Postula una historia
            </Button>
            <Link
              to="/admin/login"
              className="text-[10px] uppercase tracking-[0.25em] text-cream/50 hover:text-cream"
            >
              Admin
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
